import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
  Mail, Lock, Users, Send, CalendarDays, Sparkles, Rocket, Plus,
  Upload, Loader2, Trash2, X, ChevronLeft, ChevronRight, Eye, Smartphone,
  Monitor, ListChecks, Info, ShieldAlert, ShieldCheck, Building2, CheckCircle2, AlertCircle,
  Wand2,
} from "lucide-react";
import EmailBuilder from "./EmailBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";

// =============================================================
// LOCKED (non-owner) view
// =============================================================
function LockedComingSoon({ access }) {
  const plan = access?.plan || "free";
  const isPaidTier = plan === "pro" || plan === "business";
  return (
    <div className="max-w-3xl mx-auto" data-testid="email-marketing-locked">
      <GlassCard className="p-8 text-center">
        <div className="mx-auto h-14 w-14 rounded-xl bg-[color:rgba(27,168,159,0.10)] flex items-center justify-center text-[color:var(--brand-primary)] mb-4">
          <Mail className="h-7 w-7" />
        </div>
        <Badge variant="outline" className="mb-3 border-[color:var(--border-subtle)] text-[color:var(--text-2)]">
          <Lock className="h-3 w-3 mr-1" /> Coming Soon
        </Badge>
        <h2 className="font-display text-2xl font-semibold mb-2">Email Marketing</h2>
        <p className="text-sm text-[color:var(--text-2)] max-w-lg mx-auto">
          Reach your customers beyond social media. Create campaigns, manage your audience,
          schedule emails, and let Postmoro AI help draft your marketing — all from one place.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
          {[
            { icon: Sparkles, label: "AI-assisted email campaigns" },
            { icon: Users, label: "Customer email lists" },
            { icon: CalendarDays, label: "Email scheduling & calendar" },
            { icon: Rocket, label: "Email Marketing Autopilot" },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2">
              <f.icon className="h-4 w-4 text-[color:var(--brand-primary)]" />
              <span className="text-sm text-[color:var(--text-2)]">{f.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs text-[color:var(--text-3)]" data-testid="email-marketing-locked-plan-hint">
          {isPaidTier
            ? "Included with your plan when available."
            : "Coming Soon on Pro & Business."}
        </div>
      </GlassCard>
    </div>
  );
}

// =============================================================
// OVERVIEW
// =============================================================
function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/email/overview");
        if (!cancelled) setStats(r.data);
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const cards = [
    { label: "Total subscribers", value: stats?.total_subscribers ?? "—", icon: Users, testid: "em-stat-total" },
    { label: "Active subscribers", value: stats?.active_subscribers ?? "—", icon: Users, testid: "em-stat-active" },
    { label: "Draft campaigns", value: stats?.draft_campaigns ?? "—", icon: Mail, testid: "em-stat-drafts" },
    { label: "Scheduled campaigns", value: stats?.scheduled_campaigns ?? "—", icon: CalendarDays, testid: "em-stat-scheduled" },
    { label: "Emails sent", value: stats?.emails_sent ?? "—", icon: Send, testid: "em-stat-sent" },
  ];
  const empty = stats && stats.total_subscribers === 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <GlassCard key={c.label} className="p-4" data-testid={c.testid}>
            <div className="flex items-center gap-2 text-[color:var(--text-3)] text-xs">
              <c.icon className="h-3.5 w-3.5" /> {c.label}
            </div>
            <div className="mt-2 font-display text-2xl font-semibold">{c.value}</div>
          </GlassCard>
        ))}
      </div>
      <GlassCard className="p-5">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)] mb-1">Upcoming campaign</div>
        {stats?.upcoming_campaign ? (
          <div>
            <div className="font-display text-lg font-semibold">{stats.upcoming_campaign.name}</div>
            <div className="text-sm text-[color:var(--text-2)] mt-1">{stats.upcoming_campaign.subject}</div>
            <div className="text-xs text-[color:var(--text-3)] mt-1">Scheduled: {stats.upcoming_campaign.scheduled_at}</div>
          </div>
        ) : (
          <div className="text-sm text-[color:var(--text-2)]">
            {empty
              ? "No subscribers yet. Import your audience to start building email campaigns."
              : "No campaigns scheduled. Draft one from the Campaigns tab and schedule it here."}
          </div>
        )}
      </GlassCard>
      <div className="rounded-md border border-[color:rgba(255,176,32,0.25)] bg-[color:rgba(255,176,32,0.06)] text-[color:var(--warning)] p-3 text-xs flex items-start gap-2" data-testid="em-dev-banner">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Development preview</div>
          <div className="text-[color:var(--text-2)]">
            Email delivery is fully disabled while this feature is under construction — no
            campaigns, scheduled or otherwise, will actually send. You can safely draft,
            import contacts, and configure Autopilot.
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// LISTS + CONTACTS
// =============================================================
function ContactsTab() {
  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeList, setActiveList] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewList, setShowNewList] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const refreshLists = useCallback(async () => {
    try {
      const r = await api.get("/email/lists");
      setLists(r.data.lists || []);
    } catch (_e) { /* ignore */ }
  }, []);
  const refreshContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (activeList) params.set("list_id", activeList);
      const r = await api.get(`/email/contacts?${params.toString()}`);
      setContacts(r.data.contacts || []);
      setTotal(r.data.total || 0);
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }, [activeList]);

  useEffect(() => { refreshLists(); }, [refreshLists]);
  useEffect(() => { refreshContacts(); }, [refreshContacts]);

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Lists sidebar */}
      <GlassCard className="col-span-12 lg:col-span-3 p-3">
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)]">Lists</div>
          <Button size="sm" variant="ghost" onClick={() => setShowNewList(true)} data-testid="em-new-list-button">
            <Plus className="h-3.5 w-3.5 mr-1" /> New
          </Button>
        </div>
        <button
          className={"w-full text-left rounded-md px-3 py-2 text-sm " + (!activeList ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)]" : "text-[color:var(--text-2)] hover:bg-[color:var(--surface-1)]")}
          onClick={() => setActiveList("")}
          data-testid="em-list-all"
        >
          All contacts <span className="text-[color:var(--text-3)] ml-1">({total})</span>
        </button>
        {lists.map((l) => (
          <div key={l.id} className="flex items-center group">
            <button
              className={"flex-1 text-left rounded-md px-3 py-2 text-sm " + (activeList === l.id ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)]" : "text-[color:var(--text-2)] hover:bg-[color:var(--surface-1)]")}
              onClick={() => setActiveList(l.id)}
              data-testid={`em-list-${l.id}`}
            >
              {l.name} <span className="text-[color:var(--text-3)] ml-1">({l.contact_count})</span>
            </button>
            <button
              className="opacity-0 group-hover:opacity-100 p-1 text-[color:var(--text-3)] hover:text-[color:var(--danger)]"
              onClick={async () => {
                if (!confirm(`Delete list "${l.name}"? Contacts will remain.`)) return;
                try {
                  await api.delete(`/email/lists/${l.id}`);
                  toast.success("List deleted");
                  await refreshLists();
                  if (activeList === l.id) setActiveList("");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not delete list");
                }
              }}
              aria-label={`Delete list ${l.name}`}
              data-testid={`em-list-delete-${l.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </GlassCard>

      {/* Contacts panel */}
      <GlassCard className="col-span-12 lg:col-span-9 p-4">
        <div className="flex flex-wrap items-center gap-2 justify-between mb-3">
          <div>
            <div className="font-display text-lg font-semibold">
              {activeList ? (lists.find(l => l.id === activeList)?.name || "List") : "All contacts"}
            </div>
            <div className="text-xs text-[color:var(--text-3)]">{total} contacts</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="em-import-button">
              <Upload className="h-4 w-4 mr-2" /> Import CSV
            </Button>
            <Button onClick={() => setShowNewContact(true)} data-testid="em-new-contact-button">
              <Plus className="h-4 w-4 mr-2" /> Add contact
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--brand-primary)]" /></div>
        ) : contacts.length === 0 ? (
          <div className="py-12 text-center text-sm text-[color:var(--text-3)]" data-testid="em-contacts-empty">
            No contacts yet. Import a CSV or add one manually to start building your audience.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-[color:var(--text-3)] uppercase tracking-widest">
                <tr><th className="text-left py-2">Email</th><th className="text-left py-2">Name</th><th className="text-left py-2">Status</th><th className="text-left py-2">Source</th><th></th></tr>
              </thead>
              <tbody data-testid="em-contacts-table">
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-[color:var(--border-subtle)]" data-testid={`em-contact-${c.id}`}>
                    <td className="py-2 pr-3">{c.email}</td>
                    <td className="py-2 pr-3 text-[color:var(--text-2)]">{[c.first_name, c.last_name].filter(Boolean).join(" ")}</td>
                    <td className="py-2 pr-3">
                      <Badge variant={c.status === "subscribed" ? "default" : "outline"}>{c.status}</Badge>
                    </td>
                    <td className="py-2 pr-3 text-xs text-[color:var(--text-3)]">{c.source}</td>
                    <td className="py-2 text-right">
                      <button
                        className="text-[color:var(--text-3)] hover:text-[color:var(--danger)] p-1"
                        onClick={async () => {
                          if (!confirm(`Delete ${c.email}?`)) return;
                          try {
                            await api.delete(`/email/contacts/${c.id}`);
                            await refreshContacts();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
                        }}
                      ><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* New list dialog */}
      <NewListDialog open={showNewList} onOpenChange={setShowNewList} onCreated={() => { refreshLists(); }} />
      {/* New contact dialog */}
      <NewContactDialog open={showNewContact} onOpenChange={setShowNewContact} lists={lists} onCreated={() => { refreshContacts(); refreshLists(); }} />
      {/* Import dialog */}
      <ImportDialog open={showImport} onOpenChange={setShowImport} lists={lists} onImported={() => { refreshContacts(); refreshLists(); }} />
    </div>
  );
}

function NewListDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/email/lists", { name: name.trim(), description: desc.trim() });
      toast.success("List created");
      setName(""); setDesc(""); onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create list"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="em-new-list-dialog">
        <DialogHeader><DialogTitle>New list</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Customers, Newsletter, VIP…" data-testid="em-new-list-name" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="How you'll use this list" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!name.trim() || saving} data-testid="em-new-list-save">{saving ? "Saving…" : "Create list"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewContactDialog({ open, onOpenChange, lists, onCreated }) {
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setSaving(true);
    try {
      await api.post("/email/contacts", { email: email.trim(), first_name: first, last_name: last, list_ids: selected });
      toast.success("Contact added");
      setEmail(""); setFirst(""); setLast(""); setSelected([]); onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add contact"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="em-new-contact-dialog">
        <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Email *</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="em-new-contact-email" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>First name</Label><Input value={first} onChange={(e) => setFirst(e.target.value)} /></div>
            <div><Label>Last name</Label><Input value={last} onChange={(e) => setLast(e.target.value)} /></div>
          </div>
          {lists.length > 0 && (
            <div>
              <Label>Add to lists</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {lists.map((l) => (
                  <button key={l.id} type="button"
                    onClick={() => setSelected((prev) => prev.includes(l.id) ? prev.filter(x => x !== l.id) : [...prev, l.id])}
                    className={"text-xs rounded-full px-2 py-1 border " + (selected.includes(l.id) ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]" : "border-[color:var(--border-subtle)] text-[color:var(--text-2)]")}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!email.trim() || saving} data-testid="em-new-contact-save">{saving ? "Saving…" : "Add contact"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onOpenChange, lists, onImported }) {
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [selectedLists, setSelectedLists] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const runPreview = async () => {
    if (!csv.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/email/contacts/import", { csv_text: csv, commit: false, default_list_ids: selectedLists });
      setPreview(r.data);
      if (!r.data.ok) toast.error(r.data.error);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const runCommit = async () => {
    if (!preview?.ok) return;
    setBusy(true);
    try {
      const r = await api.post("/email/contacts/import", { csv_text: csv, commit: true, default_list_ids: selectedLists });
      toast.success(`Imported ${r.data.imported} contacts (${r.data.skipped_existing} already existed)`);
      setCsv(""); setPreview(null); onOpenChange(false); onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="em-import-dialog">
        <DialogHeader><DialogTitle>Import contacts from CSV</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-[color:var(--text-3)] flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5" />
            <span>Auto-detects <code>email</code> (required), plus optional <code>first_name</code>, <code>last_name</code>, <code>name</code>, <code>company</code>, <code>phone</code>.</span>
          </div>
          <div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const reader = new FileReader(); reader.onload = () => setCsv(String(reader.result || "")); reader.readAsText(f); }}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="em-import-file"><Upload className="h-4 w-4 mr-2" /> Choose CSV file</Button>
          </div>
          <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="Or paste CSV content here…" rows={8} data-testid="em-import-textarea" />
          {lists.length > 0 && (
            <div>
              <Label>Add imported contacts to lists</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {lists.map((l) => (
                  <button key={l.id} type="button"
                    onClick={() => setSelectedLists((prev) => prev.includes(l.id) ? prev.filter(x => x !== l.id) : [...prev, l.id])}
                    className={"text-xs rounded-full px-2 py-1 border " + (selectedLists.includes(l.id) ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]" : "border-[color:var(--border-subtle)] text-[color:var(--text-2)]")}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {preview && (
            <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs" data-testid="em-import-preview">
              {!preview.ok ? (
                <div className="text-[color:var(--danger)]">{preview.error} — supported headers: {(preview.supported_headers || []).join(", ")}</div>
              ) : (
                <div className="space-y-1">
                  <div>Detected: {Object.entries(preview.detected_headers || {}).map(([k, v]) => `${k}→${v}`).join(", ")}</div>
                  <div>Rows detected: <strong>{preview.rows_detected}</strong></div>
                  <div>Valid: <strong className="text-[color:var(--success)]">{preview.valid?.length || 0}</strong></div>
                  <div>Invalid emails skipped: <strong>{preview.invalid_count}</strong></div>
                  <div>Duplicates within CSV: <strong>{preview.duplicate_in_batch}</strong></div>
                  <div>Already in your list (will be skipped): <strong>{preview.existing_duplicates}</strong></div>
                  <div className="pt-1">Will import: <strong className="text-[color:var(--brand-primary)]">{preview.would_import}</strong></div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          {!preview?.ok ? (
            <Button onClick={runPreview} disabled={busy || !csv.trim()} data-testid="em-import-preview-btn">{busy ? "Analyzing…" : "Preview"}</Button>
          ) : (
            <Button onClick={runCommit} disabled={busy || (preview?.would_import ?? 0) === 0} data-testid="em-import-commit">
              {busy ? "Importing…" : `Import ${preview?.would_import ?? 0} contacts`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================
// CAMPAIGNS
// =============================================================
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null); // campaign object or null
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([api.get("/email/campaigns"), api.get("/email/lists")]);
      setCampaigns(c.data.campaigns || []);
      setLists(l.data.lists || []);
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const createNew = async () => {
    try {
      const r = await api.post("/email/campaigns", { name: "Untitled campaign" });
      setEditing(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create campaign"); }
  };

  if (editing) {
    return <CampaignEditor campaign={editing} lists={lists} onClose={() => { setEditing(null); refresh(); }} />;
  }
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={createNew} data-testid="em-new-campaign-button"><Plus className="h-4 w-4 mr-2" /> New campaign</Button>
      </div>
      {loading ? <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        : campaigns.length === 0 ? (
          <GlassCard className="p-8 text-center text-sm text-[color:var(--text-3)]" data-testid="em-campaigns-empty">
            No campaigns yet. Draft your first one.
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {campaigns.map((c) => (
              <GlassCard key={c.id} className="p-4 glass-hover" data-testid={`em-campaign-${c.id}`}>
                <div className="cursor-pointer" onClick={() => setEditing(c)}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="capitalize" data-testid={`em-campaign-${c.id}-status`}>{c.status}</Badge>
                    {c.scheduled_at && <span className="text-[10px] text-[color:var(--text-3)]">{String(c.scheduled_at).slice(0, 16)}</span>}
                  </div>
                  <div className="font-display text-base font-semibold truncate">{c.name || "Untitled"}</div>
                  <div className="text-sm text-[color:var(--text-2)] truncate mt-1">{c.subject || "(no subject)"}</div>
                  <div className="text-xs text-[color:var(--text-3)] truncate mt-1">{c.preview_text}</div>
                </div>
                <CampaignActions campaign={c} onChanged={refresh} />
              </GlassCard>
            ))}
          </div>
        )}
    </div>
  );
}

// -------------------------------------------------------------------
// =============================================================
// Ticket 5.1 — Owner-only Sender profile + readiness panel
// Renders inside the Email Marketing area which itself is
// gated behind the owner allowlist. Non-owners never load this
// file's tabs (see LockedComingSoon short-circuit).
// =============================================================
const SENDER_FIELDS = [
  { key: "business_name",        label: "Business name *", ph: "Postmoro" },
  { key: "display_name",         label: "Display name",     ph: "Postmoro" },
  { key: "from_email",           label: "From email",       ph: "hello@em6257.postmoro.com" },
  { key: "reply_to_email",       label: "Reply-to email",   ph: "support@postmoro.com" },
  { key: "domain",               label: "Sending domain",   ph: "em6257.postmoro.com" },
  { key: "postal_address_line1", label: "Address line 1 *", ph: "123 Main St" },
  { key: "postal_address_line2", label: "Address line 2",   ph: "Suite 4B" },
  { key: "city",                 label: "City *",            ph: "Portland" },
  { key: "region",               label: "State/Region",      ph: "OR" },
  { key: "postal_code",          label: "Postal code",       ph: "97201" },
  { key: "country",              label: "Country *",         ph: "USA" },
];

function SenderProfileTab({ access }) {
  const isOwnerDev = !!(access && access.is_owner_dev === true);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [confirmed, setConfirmed] = useState({
    domain: false, link: false, asm: false, webhook: false, signed: false,
  });
  const [smokeAck, setSmokeAck] = useState(false);
  const [smokeBusy, setSmokeBusy] = useState(false);
  const [smokeResult, setSmokeResult] = useState(null);
  const [smokeIdemKey] = useState(() => `ui_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`);
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [phase1, setPhase1] = useState(null);

  const refreshPhase1 = React.useCallback(async () => {
    if (!isOwnerDev) return;
    try {
      const { data } = await api.get("/email/phase1-status");
      setPhase1(data);
    } catch (e) { /* silent — informational card */ }
  }, [isOwnerDev]);

  const refreshAudit = React.useCallback(async () => {
    if (!isOwnerDev) return;
    setAuditBusy(true);
    try {
      const { data } = await api.get("/email/owner-smoke-audit");
      setAudit(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Audit fetch failed";
      toast.error(String(msg));
    } finally { setAuditBusy(false); }
  }, [isOwnerDev]);

  const runBackfill = React.useCallback(async () => {
    if (!isOwnerDev) return;
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      // Dry-run first
      const dry = await api.post("/email/owner-smoke-audit/backfill", { dry_run: true });
      setBackfillResult(dry.data);
      const eligible = dry.data?.eligible_event_count || 0;
      const wouldResolve = dry.data?.would_resolve_count || 0;
      if (!eligible || !wouldResolve) {
        toast.warning(`No eligible events to repair (eligible=${eligible}, would_resolve=${wouldResolve})`);
        return;
      }
      const ok = window.confirm(
        `Backfill ${wouldResolve} of ${eligible} unresolved smoke event(s)? ` +
        `This re-runs the resolver + Ticket 2 mutations locally. No email will be sent.`
      );
      if (!ok) return;
      const commit = await api.post("/email/owner-smoke-audit/backfill", { dry_run: false });
      setBackfillResult(commit.data);
      toast.success(`Backfill committed: ${commit.data?.committed_count || 0} event(s) applied`);
      // Refresh audit so the counters reflect the applied events.
      await refreshAudit();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Backfill failed";
      toast.error(String(msg));
    } finally { setBackfillBusy(false); }
  }, [isOwnerDev, refreshAudit]);

  const runSmoke = async () => {
    setSmokeBusy(true);
    try {
      const { data } = await api.post("/email/owner-smoke-send", {
        confirm_real_send: true,
        idempotency_key: smokeIdemKey,
      });
      setSmokeResult(data);
      if (data.ok) toast.success("Smoke send accepted by SendGrid");
      else toast.warning(`Smoke: ${data.smoke?.status || "failed"}`);
      // Kick off an audit refresh so the Latest smoke audit card reflects
      // this send. Webhooks may still be in flight — user can Refresh audit.
      refreshAudit();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Smoke send failed";
      toast.error(String(msg));
    } finally { setSmokeBusy(false); }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/email/sender-profile"),
        api.get("/email/live-readiness", { params: { owner_smoke: true } }),
      ]);
      const prof = p.data?.sender_profile || null;
      setProfile(prof);
      setForm(prof ? { ...prof } : {});
      setReadiness(r.data || null);
    } catch (e) {
      toast.error("Failed to load sender profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { refreshAudit(); }, [refreshAudit]);
  useEffect(() => { refreshPhase1(); }, [refreshPhase1]);

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      SENDER_FIELDS.forEach(({ key }) => {
        if (form[key] !== undefined) patch[key] = form[key] || "";
      });
      const { data } = await api.put("/email/sender-profile", patch);
      setProfile(data.sender_profile);
      const r = await api.get("/email/live-readiness", { params: { owner_smoke: true } });
      setReadiness(r.data);
      toast.success("Sender profile saved");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const allConfirmed = Object.values(confirmed).every(Boolean);
  const canMarkVerified =
    !!profile
    && !!(profile.business_name && profile.postal_address_line1
          && profile.city && profile.country)
    && allConfirmed;

  const markVerified = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/email/sender-profile/mark-verified", {
        confirm_domain_auth:   confirmed.domain,
        confirm_link_branding: confirmed.link,
        confirm_asm_group:     confirmed.asm,
        confirm_event_webhook: confirmed.webhook,
        confirm_signed_webhook: confirmed.signed,
      });
      setProfile(data.sender_profile);
      setReadiness(data.readiness);
      toast.success("SendGrid verification recorded");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Verification recording failed";
      toast.error(String(msg));
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm opacity-70" data-testid="sender-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sender-profile-tab">
      <GlassCard className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Business identity</h3>
          <Badge variant="secondary" className="ml-2">Owner only</Badge>
        </div>
        <p className="text-sm opacity-70 mb-4">
          This information is used in the marketing footer and CAN-SPAM postal
          address. It is workspace-scoped and never leaves your workspace.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SENDER_FIELDS.map(({ key, label, ph }) => (
            <div key={key}>
              <Label htmlFor={`sf-${key}`} className="text-xs opacity-80">{label}</Label>
              <Input
                id={`sf-${key}`}
                data-testid={`sender-field-${key}`}
                placeholder={ph}
                value={form[key] || ""}
                onChange={(e) => onField(key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="sender-save-button"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save profile
          </Button>
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Record SendGrid verification</h3>
        </div>
        <p className="text-sm opacity-70 mb-4">
          Confirm each dashboard fact below, then press Record verification.
          This never contacts SendGrid — it records what you've verified
          manually. Idempotent; safe to press twice.
        </p>
        <div className="space-y-2 text-sm">
          {[
            { k: "domain",  label: "Domain Authentication verified — em6257.postmoro.com" },
            { k: "link",    label: "Link Branding verified — postmoro.com" },
            { k: "asm",     label: "ASM group verified — 'Postmoro Marketing & Updates' (ID 40402, default)" },
            { k: "webhook", label: "Event Webhook enabled — /api/email/webhooks/sendgrid" },
            { k: "signed",  label: "Signed Event Webhook enabled" },
          ].map(({ k, label }) => (
            <label key={k} className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed[k]}
                onChange={(e) => setConfirmed((c) => ({ ...c, [k]: e.target.checked }))}
                data-testid={`sender-confirm-${k}`}
                className="mt-1"
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="text-xs opacity-60 mt-3">
          IP type: Shared IP. Reverse DNS not available on current plan.
        </div>
        {profile?.verification_status === "verified"
          && profile?.domain_auth_status === "verified" ? (
          <div className="mt-4 flex items-center gap-2 text-emerald-400 text-sm" data-testid="sender-already-verified">
            <CheckCircle2 className="h-4 w-4" />
            Verification recorded ({profile.verification_status} / {profile.domain_auth_status})
          </div>
        ) : null}
        <div className="mt-4 flex justify-end">
          <Button
            onClick={markVerified}
            disabled={!canMarkVerified || verifying}
            data-testid="sender-mark-verified-button"
          >
            {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Record verification
          </Button>
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Live-send readiness</h3>
        </div>
        {readiness ? (
          <div>
            <div className="flex items-center gap-2 mb-3 text-sm">
              <span>Status:</span>
              {readiness.ready ? (
                <span className="text-emerald-400 font-semibold" data-testid="readiness-ready">READY</span>
              ) : (
                <span className="text-amber-400 font-semibold" data-testid="readiness-not-ready">NOT READY</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs opacity-80 mb-3">
              <div>feature_live: <code>{String(readiness.feature_live)}</code></div>
              <div>send_mode: <code>{readiness.send_mode}</code></div>
              <div>owner_smoke_gate: <code>{String(readiness.owner_smoke_gate)}</code></div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Blockers</div>
              {(!readiness.blockers || readiness.blockers.length === 0) ? (
                <div className="text-sm opacity-70">None — ready for live send.</div>
              ) : (
                <ul className="text-sm space-y-1" data-testid="readiness-blockers-list">
                  {readiness.blockers.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
                      <code className="text-xs">{b}</code>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="text-xs opacity-60 mt-3" data-testid="readiness-gate-copy">
              {readiness.owner_smoke_gate === true && readiness.ready ? (
                <>Owner smoke testing is enabled. Normal customer Email Marketing remains locked (<code>_EMAIL_MARKETING_FEATURE_LIVE = {String(readiness.feature_live)}</code>, <code>MARKETING_SEND_MODE = {readiness.send_mode}</code>).</>
              ) : readiness.owner_smoke_gate === true ? (
                <>Owner smoke gate is enabled but readiness is not ready — resolve the blockers above before sending a real test. Normal customer Email Marketing remains locked.</>
              ) : (
                <>Owner live-smoke gate is disabled (<code>EMAIL_MARKETING_OWNER_LIVE_SMOKE_ENABLED = false</code>). Normal customer Email Marketing remains locked.</>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm opacity-70">Readiness unavailable</div>
        )}
      </GlassCard>

      {isOwnerDev ? (
      <GlassCard className="p-5" data-testid="owner-smoke-card">
        <div className="flex items-center gap-2 mb-2">
          <Rocket className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Owner smoke test</h3>
          <Badge variant="secondary" className="ml-2">Owner only · Live send</Badge>
        </div>
        <p className="text-sm opacity-70 mb-2">
          Sends <strong>one real email</strong> through SendGrid to your
          owner test address (server-resolved from your signed-in
          account). Cooldown 5 minutes. Not visible to customers.
        </p>
        <div className="text-xs opacity-70 mb-3">
          Readiness: {readiness?.ready ? (
            <span className="text-emerald-400 font-semibold">READY</span>
          ) : (
            <span className="text-amber-400 font-semibold">NOT READY</span>
          )}
        </div>
        <label className="flex items-start gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={smokeAck}
            onChange={(e) => setSmokeAck(e.target.checked)}
            data-testid="smoke-ack" className="mt-1" />
          <span>I understand this sends one real email through SendGrid.</span>
        </label>
        {smokeResult ? (
          <div className="mt-3 text-sm" data-testid="smoke-result">
            <div>Status: <strong>{smokeResult.smoke?.status}</strong></div>
            {smokeResult.smoke?.provider_message_id ? (
              <div>Provider request id: <code>{smokeResult.smoke.provider_message_id}</code></div>
            ) : null}
            <div>Delivery id: <code>{smokeResult.smoke?.delivery_id}</code></div>
            {smokeResult.idempotent_replay ? (
              <div className="opacity-70 text-xs mt-1">(Idempotent replay — no new send)</div>
            ) : null}
          </div>
        ) : null}
        <div className="mt-4 flex justify-end">
          <Button onClick={runSmoke}
            disabled={!smokeAck || !readiness?.ready || smokeBusy}
            data-testid="smoke-send-button">
            {smokeBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Send 1 real test email
          </Button>
        </div>
      </GlassCard>
      ) : null}

      {isOwnerDev ? (
      <GlassCard className="p-5" data-testid="owner-smoke-audit-card">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Latest smoke audit</h3>
          <Badge variant="secondary" className="ml-2">Owner only · Read-only</Badge>
        </div>
        <p className="text-sm opacity-70 mb-3">
          Read-only summary of the most recent real owner smoke send in
          this workspace plus its Ticket 2 webhook events. Nothing is sent
          or mutated by this view.
        </p>
        {audit === null && auditBusy ? (
          <div className="text-sm opacity-70 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading audit…
          </div>
        ) : null}
        {audit && audit.found === false ? (
          <div className="text-sm opacity-70" data-testid="audit-no-smoke">
            No smoke send has been recorded for this workspace yet.
          </div>
        ) : null}
        {audit && audit.found === true ? (
          <div className="space-y-3" data-testid="audit-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div>Smoke status: <strong data-testid="audit-smoke-status">{audit.smoke?.status || "—"}</strong></div>
              <div>Delivery status: <strong data-testid="audit-delivery-status">{audit.delivery?.status || "—"}</strong></div>
              <div className="truncate">Provider message id: <code className="text-xs">{audit.smoke?.provider_message_id || "—"}</code></div>
              <div className="truncate">Delivery id: <code className="text-xs">{audit.smoke?.delivery_id || "—"}</code></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs opacity-90">
              {["processed","delivered","open","click","bounce","spam_report","unsubscribe","dropped","deferred","group_unsubscribe","group_resubscribe"].map((k) => (
                <div key={k} className="border border-[color:var(--border-subtle)] rounded-md px-2 py-1"
                     data-testid={`audit-tally-${k}`}>
                  <div className="opacity-70">{k}</div>
                  <div className="font-semibold">{audit.events?.tally?.[k] ?? 0}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs opacity-90">
              <div data-testid="audit-webhook-signature">Webhook signature: <strong>{String(audit.webhook?.signature_validation_succeeded === true)}</strong></div>
              <div data-testid="audit-webhook-ws">Workspace resolved: <strong>{String(audit.webhook?.workspace_resolution_succeeded === true)}</strong></div>
              <div data-testid="audit-webhook-delivery">Delivery resolved: <strong>{String(audit.webhook?.delivery_resolution_succeeded === true)}</strong></div>
            </div>
            <div className="text-xs opacity-90">
              Duplicate events: <strong data-testid="audit-dup-flag">{String(audit.duplicates?.duplicate_events_detected === true)}</strong>
              {audit.duplicates?.duplicate_events_detected ? (
                <span className="ml-2 text-amber-400">({Object.keys(audit.duplicates.duplicate_event_ids || {}).length})</span>
              ) : null}
            </div>
            {(audit.errors || []).length > 0 ? (
              <div className="text-xs">
                <div className="mb-1 opacity-70">Errors:</div>
                <ul className="space-y-1" data-testid="audit-errors">
                  {audit.errors.map((e, i) => (
                    <li key={i}><code>{e.event_type}</code>: {e.mismatch_reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className={"text-sm font-semibold " + (audit.ticket_6_e2e_pass ? "text-emerald-400" : "text-amber-400")}
                 data-testid="audit-e2e-flag">
              TICKET 6 END-TO-END: {audit.ticket_6_e2e_pass ? "PASS" : "UNVERIFIED"}
            </div>
            {audit.diagnostics ? (
              <div className="border border-[color:var(--border-subtle)] rounded-md p-3 mt-2" data-testid="audit-diagnostics">
                <div className="text-sm font-medium mb-1">Diagnostics</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs opacity-90">
                  <div data-testid="diag-recent-count">Recent events found: <strong>{audit.diagnostics.recent_events_found}</strong></div>
                  <div data-testid="diag-unmatched-count">Unmatched events: <strong>{audit.diagnostics.unmatched_events_count}</strong></div>
                  <div>Last event type: <strong>{audit.diagnostics.last_event_type || "—"}</strong></div>
                  <div className="truncate">Last event at: <span>{audit.diagnostics.last_event_at || "—"}</span></div>
                  <div className="md:col-span-2 truncate">Last mismatch reason: <strong>{audit.diagnostics.last_mismatch_reason || "—"}</strong></div>
                </div>
                {(audit.diagnostics.recent_events || []).length > 0 ? (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer opacity-80">Show recent {audit.diagnostics.recent_events.length} events</summary>
                    <ul className="mt-1 space-y-1" data-testid="diag-recent-list">
                      {audit.diagnostics.recent_events.map((e, i) => (
                        <li key={i} className="opacity-90">
                          <code>{e.event_type || "—"}</code>{" · applied="}<strong>{String(!!e.applied)}</strong>
                          {e.mismatch_reason ? <> · <span className="text-amber-400">{e.mismatch_reason}</span></> : null}
                          {e.delivery_id ? <> · d=<code>{String(e.delivery_id).slice(0, 12)}…</code></> : null}
                          {e.sg_message_id ? <> · sgm=<code>{String(e.sg_message_id).slice(0, 12)}…</code></> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                {(audit.diagnostics.unmatched_events_count || 0) > 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={runBackfill}
                            disabled={backfillBusy}
                            data-testid="audit-repair-button">
                      {backfillBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Repair existing webhook events
                    </Button>
                    {backfillResult ? (
                      <span className="text-xs opacity-90" data-testid="audit-repair-result">
                        {backfillResult.dry_run ? "Dry-run: " : "Committed: "}
                        eligible={backfillResult.eligible_event_count},
                        {" "}would_resolve={backfillResult.would_resolve_count},
                        {" "}still_fail={backfillResult.would_still_fail_count}
                        {backfillResult.committed_count !== undefined ? <>, committed={backfillResult.committed_count}</> : null}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="mt-4 flex justify-end">
          <Button onClick={refreshAudit} disabled={auditBusy}
                  variant="outline" data-testid="audit-refresh-button">
            {auditBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Refresh audit
          </Button>
        </div>
      </GlassCard>
      ) : null}

      {isOwnerDev && phase1 ? (
      <GlassCard className="p-5" data-testid="phase1-status-card">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Phase 1 status</h3>
          <Badge variant="secondary" className="ml-2">Owner only · Informational</Badge>
        </div>
        <p className="text-sm opacity-70 mb-3">
          Read-only summary of every Phase 1 subsystem. No new send controls,
          no flag changes. Customer live sending remains locked at the code level.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {Object.entries(phase1.subsystems || {}).map(([key, val]) => (
            <div key={key} className="border border-[color:var(--border-subtle)] rounded-md px-3 py-2"
                 data-testid={`phase1-sub-${key}`}>
              <div className="flex items-center gap-2">
                <span className={"inline-block w-2 h-2 rounded-full " + (val.ready ? "bg-emerald-400" : "bg-amber-400")} />
                <span className="font-medium">{key.replace(/_/g, " ")}</span>
                <span className={"ml-auto text-xs " + (val.ready ? "text-emerald-400" : "text-amber-400")}>
                  {key === "customer_live_sending" ? (val.locked ? "LOCKED" : "UNLOCKED") : (val.ready ? "READY" : "NOT READY")}
                </span>
              </div>
              <div className="text-xs opacity-70 mt-1">{val.note}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs opacity-80" data-testid="phase1-summary">
          feature_live=<strong>{String(phase1.feature_live)}</strong>
          {" · "}send_mode=<strong>{phase1.send_mode}</strong>
          {" · "}owner_smoke_gate=<strong>{String(phase1.owner_smoke_gate)}</strong>
          {" · "}phase1_ready=<strong>{String(phase1.phase1_ready)}</strong>
        </div>
      </GlassCard>
      ) : null}
    </div>
  );
}


// =============================================================
// Iteration 82 Phase 1 Ticket 1 — minimal owner-only campaign
// actions: Send / Schedule / Pause / Cancel. Talks to the MOCKED
// backend engine. Fails loudly if MARKETING_SEND_MODE isn't mocked.
// -------------------------------------------------------------------
function CampaignActions({ campaign, onChanged }) {
  const [busy, setBusy] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [status, setStatus] = useState(null);

  const refreshStatus = React.useCallback(async () => {
    try {
      const r = await api.get(`/email/campaigns/${campaign.id}/status`);
      setStatus(r.data);
    } catch (_) { /* non-fatal */ }
  }, [campaign.id]);

  React.useEffect(() => { refreshStatus(); }, [refreshStatus, campaign.status]);

  const run = async (action, body) => {
    setBusy(action);
    try {
      const r = await api.post(`/email/campaigns/${campaign.id}/${action}`, body || {});
      toast.success(`Campaign ${r.data.status || action}.`);
      await refreshStatus();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || `Could not ${action} campaign`));
    } finally { setBusy(""); }
  };

  const canSend = ["draft", "scheduled"].includes(campaign.status);
  const canPause = ["scheduled", "sending"].includes(campaign.status);
  const canCancel = !["sent", "canceled", "failed"].includes(campaign.status);

  const submitSchedule = async (e) => {
    e?.preventDefault?.();
    if (!scheduledAt) return;
    await run("schedule", { scheduled_at: scheduledAt, timezone: tz });
    setShowSchedule(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-[color:var(--border-subtle)] space-y-2" data-testid={`em-campaign-${campaign.id}-actions`}>
      {status?.deliveries_by_status && Object.keys(status.deliveries_by_status).length ? (
        <div className="text-[10px] text-[color:var(--text-3)] font-mono" data-testid={`em-campaign-${campaign.id}-stats`}>
          {Object.entries(status.deliveries_by_status).map(([k, v]) => `${k}:${v}`).join(" · ")}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" disabled={!canSend || busy === "send"} onClick={() => run("send")}
                data-testid={`em-campaign-${campaign.id}-send`}>
          {busy === "send" ? "Sending…" : "Send"}
        </Button>
        <Button size="sm" variant="outline" disabled={!canSend || busy === "schedule"} onClick={() => setShowSchedule((v) => !v)}
                data-testid={`em-campaign-${campaign.id}-schedule-toggle`}>
          Schedule
        </Button>
        <Button size="sm" variant="outline" disabled={!canPause || busy === "pause"} onClick={() => run("pause")}
                data-testid={`em-campaign-${campaign.id}-pause`}>
          Pause
        </Button>
        <Button size="sm" variant="outline" disabled={!canCancel || busy === "cancel"} onClick={() => run("cancel")}
                data-testid={`em-campaign-${campaign.id}-cancel`}
                className="text-[color:var(--danger)]">
          Cancel
        </Button>
      </div>
      {showSchedule ? (
        <form onSubmit={submitSchedule} className="flex flex-wrap gap-1.5 items-center" data-testid={`em-campaign-${campaign.id}-schedule-form`}>
          <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
                 className="text-xs px-2 py-1 rounded bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]"
                 data-testid={`em-campaign-${campaign.id}-schedule-input`} required />
          <input type="text" value={tz} onChange={(e) => setTz(e.target.value)}
                 className="text-xs px-2 py-1 rounded bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] w-40"
                 placeholder="America/Toronto" data-testid={`em-campaign-${campaign.id}-schedule-tz`} required />
          <Button size="sm" type="submit" disabled={busy === "schedule"}
                  data-testid={`em-campaign-${campaign.id}-schedule-submit`}>
            {busy === "schedule" ? "…" : "Save"}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

function CampaignEditor({ campaign, lists, onClose }) {
  const [c, setC] = useState(campaign);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiGoal, setAiGoal] = useState("");
  const [previewMode, setPreviewMode] = useState("desktop"); // desktop | mobile
  const set = (patch) => setC((prev) => ({ ...prev, ...patch }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const r = await api.patch(`/email/campaigns/${c.id}`, {
        name: c.name, subject: c.subject, preview_text: c.preview_text,
        from_name: c.from_name, reply_to: c.reply_to, list_ids: c.list_ids,
        body_html: c.body_html, cta_text: c.cta_text, cta_url: c.cta_url,
        image_url: c.image_url, scheduled_at: c.scheduled_at, timezone: c.timezone,
        ...extra,
      });
      setC(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm("Delete this campaign?")) return;
    try {
      await api.delete(`/email/campaigns/${c.id}`);
      toast.success("Deleted");
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const genAi = async () => {
    setAiBusy(true);
    try {
      const audience = lists.find(l => c.list_ids?.[0] === l.id)?.name || "";
      const r = await api.post("/email/campaigns/generate-draft", {
        goal: aiGoal || c.name || "Send a helpful update to customers",
        audience_name: audience,
      });
      const d = r.data;
      setC((prev) => ({ ...prev, subject: d.subject || prev.subject, preview_text: d.preview_text || prev.preview_text, body_html: d.body_html || prev.body_html, cta_text: d.cta_text || prev.cta_text, cta_url: d.cta_url || prev.cta_url }));
      toast.success("Draft generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="em-campaign-editor">
      <div className="col-span-12 lg:col-span-6 space-y-3">
        <div className="flex items-center gap-2 justify-between">
          <Button variant="ghost" onClick={onClose} data-testid="em-editor-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={remove} data-testid="em-editor-delete"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
            <Button onClick={() => save()} disabled={saving} data-testid="em-editor-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>

        <GlassCard className="p-4 space-y-3">
          <div><Label>Campaign name</Label><Input value={c.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="em-editor-name" /></div>
          <div><Label>Subject</Label><Input value={c.subject || ""} onChange={(e) => set({ subject: e.target.value })} data-testid="em-editor-subject" /></div>
          <div><Label>Preview text</Label><Input value={c.preview_text || ""} onChange={(e) => set({ preview_text: e.target.value })} data-testid="em-editor-preview-text" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>From name</Label><Input value={c.from_name || ""} onChange={(e) => set({ from_name: e.target.value })} /></div>
            <div><Label>Reply-to</Label><Input value={c.reply_to || ""} onChange={(e) => set({ reply_to: e.target.value })} /></div>
          </div>
          <div>
            <Label>Audience</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {lists.map((l) => (
                <button key={l.id} type="button"
                  onClick={() => set({ list_ids: (c.list_ids || []).includes(l.id) ? (c.list_ids || []).filter(x => x !== l.id) : [...(c.list_ids || []), l.id] })}
                  className={"text-xs rounded-full px-2 py-1 border " + ((c.list_ids || []).includes(l.id) ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]" : "border-[color:var(--border-subtle)] text-[color:var(--text-2)]")}>
                  {l.name} ({l.contact_count})
                </button>
              ))}
              {lists.length === 0 && <span className="text-xs text-[color:var(--text-3)]">Create a list first in the Contacts tab.</span>}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)]">Email body</div>
            <div className="flex items-center gap-2">
              <Input value={aiGoal} onChange={(e) => setAiGoal(e.target.value)} placeholder="Optional goal (e.g. Weekend flash sale)" className="w-56 text-xs" data-testid="em-ai-goal" />
              <Button size="sm" variant="outline" onClick={genAi} disabled={aiBusy} data-testid="em-ai-generate">
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Generate AI draft
              </Button>
            </div>
          </div>
          <Textarea value={c.body_html || ""} onChange={(e) => set({ body_html: e.target.value })} rows={12} placeholder="Write your email body (HTML supported)…" data-testid="em-editor-body" />
          <div className="grid grid-cols-2 gap-2">
            <div><Label>CTA button text</Label><Input value={c.cta_text || ""} onChange={(e) => set({ cta_text: e.target.value })} data-testid="em-editor-cta-text" /></div>
            <div><Label>CTA URL</Label><Input value={c.cta_url || ""} onChange={(e) => set({ cta_url: e.target.value })} placeholder="https://…" /></div>
          </div>
          <div><Label>Optional image URL</Label><Input value={c.image_url || ""} onChange={(e) => set({ image_url: e.target.value })} placeholder="https://…" /></div>
        </GlassCard>

        <GlassCard className="p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)]">Schedule</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Send at (date & time)</Label>
              <Input type="datetime-local" value={c.scheduled_at || ""} onChange={(e) => set({ scheduled_at: e.target.value })} data-testid="em-editor-scheduled-at" />
            </div>
            <div>
              <Label>Timezone</Label>
              <Input value={c.timezone || ""} onChange={(e) => set({ timezone: e.target.value })} placeholder="e.g. America/New_York" />
            </div>
          </div>
          <div className="text-xs text-[color:var(--warning)] flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Scheduled campaigns are saved as data only. During development preview, no email will actually be sent at the scheduled time.</span>
          </div>
        </GlassCard>
      </div>

      {/* Preview pane */}
      <div className="col-span-12 lg:col-span-6">
        <GlassCard className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)]"><Eye className="h-3.5 w-3.5 inline mr-1" /> Preview</div>
            <div className="flex items-center gap-1 border border-[color:var(--border-subtle)] rounded-md p-0.5">
              <button className={"px-2 py-1 rounded text-xs flex items-center gap-1 " + (previewMode === "desktop" ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)]" : "text-[color:var(--text-3)]")} onClick={() => setPreviewMode("desktop")} data-testid="em-preview-desktop"><Monitor className="h-3.5 w-3.5" /> Desktop</button>
              <button className={"px-2 py-1 rounded text-xs flex items-center gap-1 " + (previewMode === "mobile" ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)]" : "text-[color:var(--text-3)]")} onClick={() => setPreviewMode("mobile")} data-testid="em-preview-mobile"><Smartphone className="h-3.5 w-3.5" /> Mobile</button>
            </div>
          </div>
          <div className={"mx-auto rounded-lg border border-[color:var(--border-subtle)] bg-white text-slate-900 overflow-hidden " + (previewMode === "mobile" ? "max-w-[360px]" : "max-w-[640px]")}
            data-testid="em-preview-frame">
            <div className="px-4 py-3 border-b border-slate-200 text-xs text-slate-500">
              <div><strong className="text-slate-900">{c.from_name || "Your business"}</strong></div>
              <div className="text-slate-700 font-semibold">{c.subject || "(no subject)"}</div>
              {c.preview_text && <div className="text-slate-400">{c.preview_text}</div>}
            </div>
            <div className="p-5 text-sm">
              {c.image_url && (
                <img alt="" src={c.image_url} className="w-full rounded-md mb-3" onError={(e) => { e.target.style.display = "none"; }} />
              )}
              <div
                data-testid="em-preview-body"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    c.body_html || "<p style='color:#94a3b8'>Start writing your email body or generate an AI draft…</p>",
                    {
                      // DOMPurify already strips <script>, on* handlers and
                      // javascript:/data: script URLs by default. We also drop
                      // <style> (prevents email CSS bleeding into the whole app
                      // shell) and other executable/embed elements, while KEEPING
                      // inline style attributes + <img>/<a> so normal email
                      // formatting is preserved.
                      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
                      ALLOW_DATA_ATTR: false,
                    },
                  ),
                }}
              />
              {c.cta_text && c.cta_url && (
                <div className="mt-4">
                  <a href={c.cta_url} target="_blank" rel="noopener noreferrer"
                    className="inline-block rounded-md bg-slate-900 text-white px-4 py-2 text-sm font-semibold no-underline">
                    {c.cta_text}
                  </a>
                </div>
              )}
              <div className="mt-6 pt-3 border-t border-slate-200 text-[10px] text-slate-400">
                You&apos;re receiving this preview inside Postmoro. Real recipients will see an unsubscribe link here.
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

// =============================================================
// CALENDAR
// =============================================================
function CalendarTab() {
  const [events, setEvents] = useState([]);
  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/email/calendar");
        setEvents(r.data.events || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);
  const monthLabel = monthCursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const daysInMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0).getDate();
  const startDow = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1).getDay();
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.scheduled_at) continue;
      const d = new Date(e.scheduled_at);
      if (isNaN(d.getTime())) continue;
      if (d.getFullYear() !== monthCursor.getFullYear() || d.getMonth() !== monthCursor.getMonth()) continue;
      const day = d.getDate();
      (map[day] = map[day] || []).push(e);
    }
    return map;
  }, [events, monthCursor]);
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))} data-testid="em-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="font-display text-lg font-semibold" data-testid="em-cal-label">{monthLabel}</div>
        <Button variant="ghost" size="icon" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))} data-testid="em-cal-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <GlassCard className="p-3">
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase text-[color:var(--text-3)] mb-1">
          {["S","M","T","W","T","F","S"].map((d, i) => <div key={i} className="text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="em-calendar-grid">
          {cells.map((c, idx) => (
            <div key={idx} className={"min-h-[64px] rounded-md p-1 text-xs " + (c ? "bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]" : "")}>
              {c && (
                <>
                  <div className="text-[color:var(--text-3)]">{c}</div>
                  {(eventsByDay[c] || []).map((e) => (
                    <div key={e.id} className="mt-0.5 truncate rounded-sm bg-[color:rgba(27,168,159,0.15)] text-[color:var(--brand-primary)] px-1 py-0.5 text-[10px]" title={e.name}>
                      {e.name}
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </GlassCard>
      <GlassCard className="p-4">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)] mb-2">Upcoming</div>
        {events.length === 0 ? (
          <div className="text-sm text-[color:var(--text-3)]" data-testid="em-cal-empty">No scheduled campaigns yet. Schedule one from the Campaigns tab.</div>
        ) : (
          <ul className="space-y-2">
            {events.map((e) => (
              <li key={e.id} className="flex items-center gap-3">
                <Badge variant="outline" className="capitalize">{e.status}</Badge>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{e.name}</div>
                  <div className="text-xs text-[color:var(--text-3)]">{e.subject}</div>
                </div>
                <div className="text-xs text-[color:var(--text-2)]">{e.scheduled_at}</div>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </div>
  );
}

// =============================================================
// AUTOPILOT
// =============================================================
function AutopilotTab() {
  const [s, setS] = useState(null);
  const [lists, setLists] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));
  useEffect(() => {
    (async () => {
      try {
        const [r, l] = await Promise.all([api.get("/email/autopilot"), api.get("/email/lists")]);
        setS(r.data);
        setLists(l.data.lists || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: !!s.enabled, goal: s.goal || "", audience_list_id: s.audience_list_id || "",
        frequency: s.frequency || "weekly", preferred_days: s.preferred_days || [],
        preferred_time: s.preferred_time || "10:00", timezone: s.timezone || "",
        approval_mode: s.approval_mode || "draft_for_approval",
      };
      const r = await api.patch("/email/autopilot", body);
      setS(r.data);
      toast.success("Autopilot saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  const days = [{k:"mon",l:"Mon"},{k:"tue",l:"Tue"},{k:"wed",l:"Wed"},{k:"thu",l:"Thu"},{k:"fri",l:"Fri"},{k:"sat",l:"Sat"},{k:"sun",l:"Sun"}];
  return (
    <div className="grid grid-cols-12 gap-4">
      <GlassCard className="col-span-12 lg:col-span-8 p-4 space-y-4">
        <div>
          <Label>Goal / instructions</Label>
          <Textarea value={s.goal || ""} onChange={(e) => set({ goal: e.target.value })} rows={3} placeholder='e.g. "Send a customer newsletter every Friday morning."' data-testid="em-ap-goal" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Audience</Label>
            <Select value={s.audience_list_id || ""} onValueChange={(v) => set({ audience_list_id: v })}>
              <SelectTrigger data-testid="em-ap-audience"><SelectValue placeholder="Select a list" /></SelectTrigger>
              <SelectContent>
                {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Frequency</Label>
            <Select value={s.frequency || "weekly"} onValueChange={(v) => set({ frequency: v })}>
              <SelectTrigger data-testid="em-ap-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Biweekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Preferred days</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {days.map((d) => (
              <button type="button" key={d.k}
                onClick={() => set({ preferred_days: (s.preferred_days || []).includes(d.k) ? (s.preferred_days || []).filter(x => x !== d.k) : [...(s.preferred_days || []), d.k] })}
                className={"text-xs rounded-full px-2 py-1 border " + ((s.preferred_days || []).includes(d.k) ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]" : "border-[color:var(--border-subtle)] text-[color:var(--text-2)]")}>
                {d.l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Preferred time</Label>
            <Input type="time" value={s.preferred_time || "10:00"} onChange={(e) => set({ preferred_time: e.target.value })} data-testid="em-ap-time" />
          </div>
          <div>
            <Label>Timezone</Label>
            <Input value={s.timezone || ""} onChange={(e) => set({ timezone: e.target.value })} placeholder="e.g. America/New_York" />
          </div>
        </div>
      </GlassCard>
      <GlassCard className="col-span-12 lg:col-span-4 p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-3)]">Approval mode</div>
        <label className="flex items-start gap-2 rounded-md border border-[color:var(--border-subtle)] p-3 cursor-pointer">
          <input type="radio" checked={s.approval_mode !== "auto_send_dev_disabled"}
            onChange={() => set({ approval_mode: "draft_for_approval" })} data-testid="em-ap-mode-draft" />
          <div>
            <div className="text-sm font-semibold">Draft for approval</div>
            <div className="text-xs text-[color:var(--text-3)]">Postmoro generates campaigns automatically. Nothing is sent until you approve each one.</div>
          </div>
        </label>
        <label className="flex items-start gap-2 rounded-md border border-[color:var(--border-subtle)] p-3 opacity-60 cursor-not-allowed" data-testid="em-ap-mode-autosend">
          <input type="radio" disabled checked={s.approval_mode === "auto_send_dev_disabled"} onChange={() => {}} />
          <div>
            <div className="text-sm font-semibold flex items-center gap-1">Auto-send <Badge variant="outline" className="ml-1">Development preview</Badge></div>
            <div className="text-xs text-[color:var(--text-3)]">Coming later. Auto-send is disabled during Phase 1 development — nothing will send even if selected.</div>
          </div>
        </label>
        <div className="rounded-md border border-[color:rgba(255,176,32,0.25)] bg-[color:rgba(255,176,32,0.06)] text-[color:var(--warning)] p-3 text-xs flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Autopilot draft mode may generate drafts. No real email will be delivered while the module is in development.</span>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <input id="ap-enabled" type="checkbox" checked={!!s.enabled} onChange={(e) => set({ enabled: e.target.checked })} data-testid="em-ap-enabled" />
          <label htmlFor="ap-enabled" className="text-sm">Enable Autopilot draft generation</label>
        </div>
        <Button onClick={save} disabled={saving} data-testid="em-ap-save">{saving ? "Saving…" : "Save Autopilot"}</Button>
      </GlassCard>
    </div>
  );
}

// =============================================================
// PAGE
// =============================================================
export default function EmailMarketingPage() {
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/email/access");
        setAccess(r.data);
      } catch (_e) { setAccess({ allowed: false, reason: "coming_soon", plan: "free" }); }
    })();
  }, []);
  if (!access) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--brand-primary)]" /></div>;

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Email Marketing"
          subtitle={access.allowed ? "Reach your customers with campaigns, lists and AI drafts." : "Coming Soon — this feature is under development."}
          testidPrefix="email-marketing"
        />
        {!access.allowed ? <LockedComingSoon access={access} /> : (
          <Tabs value={tab} onValueChange={setTab} data-testid="email-marketing-tabs">
            <TabsList className="mb-4">
              <TabsTrigger value="overview" data-testid="em-tab-overview"><ListChecks className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="em-tab-contacts"><Users className="h-4 w-4 mr-1" /> Contacts</TabsTrigger>
              <TabsTrigger value="campaigns" data-testid="em-tab-campaigns"><Mail className="h-4 w-4 mr-1" /> Campaigns</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="em-tab-calendar"><CalendarDays className="h-4 w-4 mr-1" /> Calendar</TabsTrigger>
              <TabsTrigger value="autopilot" data-testid="em-tab-autopilot"><Rocket className="h-4 w-4 mr-1" /> Autopilot</TabsTrigger>
              <TabsTrigger value="sender" data-testid="em-tab-sender"><Building2 className="h-4 w-4 mr-1" /> Sender</TabsTrigger>
              {access?.is_owner_dev ? (
                <TabsTrigger value="builder" data-testid="em-tab-builder"><Wand2 className="h-4 w-4 mr-1" /> Create Email</TabsTrigger>
              ) : null}
            </TabsList>
            <TabsContent value="overview"><OverviewTab /></TabsContent>
            <TabsContent value="contacts"><ContactsTab /></TabsContent>
            <TabsContent value="campaigns"><CampaignsTab /></TabsContent>
            <TabsContent value="calendar"><CalendarTab /></TabsContent>
            <TabsContent value="autopilot"><AutopilotTab /></TabsContent>
            <TabsContent value="sender"><SenderProfileTab access={access} /></TabsContent>
            {access?.is_owner_dev ? (
              <TabsContent value="builder" data-testid="em-tab-content-builder">
                <EmailBuilder isOwnerDev={!!access?.is_owner_dev} />
              </TabsContent>
            ) : null}
          </Tabs>
        )}
      </div>
    </div>
  );
}
